'use es6';

import { ACTION_HUB_OPT_IN, ACTION_HUB_OPT_OUT, ACTION_USER_OPT_OUT } from 'ui-addon-opt/state/OptActions';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { getFeedbackLoader, showFeedbackSurvey } from 'ui-addon-opt/lib/Feedback';
import { trackFeedbackSurveyClick } from 'ui-addon-opt/lib/Tracking';
import { useFeedbackSurveyAvailabilityEffect } from 'ui-addon-opt/effects/OptEffects';
import AdditionalOptionsPropType from './AdditionalOptionsPropType';
import { BetaButton } from './BetaButton';
import FormattedMessage from 'I18n/components/FormattedMessage';
import PropTypes from 'prop-types';
import { Button } from 'foundations-components/transitional/components/Button';
import { Dropdown } from 'foundations-components/transitional/components/Dropdown';
import { List } from 'foundations-components/transitional/components/List';
import emptyFunction from 'react-utils/emptyFunction';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const defaultOptOutButtonText = /*#__PURE__*/_jsx(FormattedMessage, {
  message: "ui-addon-opt.optout.label"
});
const Optout = ({
  additionalOptions,
  dispatch,
  feedbackSurveyId,
  learnMore,
  onLearnMoreClick = emptyFunction,
  onMinimized = emptyFunction,
  onView = emptyFunction,
  optinName,
  optOutButtonText = defaultOptOutButtonText,
  showHubOptin = false,
  showHubOptout = false,
  showUserOptout = true,
  usageTracker
}) => {
  const [feedbackClient, setFeedbackClient] = useState(null);
  const [dropdownOpened, setDropdownOpened] = useState(false);
  const feedbackSurveyAvailable = useFeedbackSurveyAvailabilityEffect(dropdownOpened ? feedbackClient : null, dropdownOpened ? feedbackSurveyId : null);
  const onFeedbackClientLoadCb = useCallback(client => {
    setFeedbackClient(client);
  }, []);
  useEffect(() => {
    if (!dropdownOpened) {
      return;
    }

    // onFeedbackClientLoadCb will not be invoked if the client is already loaded by the app
    // in that case, fall back to the global instance
    if (feedbackSurveyId && !feedbackClient && window.hsFeedback) {
      setFeedbackClient(window.hsFeedback);
    }
  }, [feedbackSurveyId, feedbackClient, dropdownOpened]);
  const onFeedbackSurveyClickCb = useCallback(() => {
    showFeedbackSurvey(feedbackClient, feedbackSurveyId);
    trackFeedbackSurveyClick(usageTracker, optinName);
  }, [feedbackClient, feedbackSurveyId, optinName, usageTracker]);
  const onDropdownOpen = useCallback(() => {
    setDropdownOpened(true);
    onView();
  }, [onView]);
  const FeedbackLoader = useMemo(() => feedbackSurveyId && dropdownOpened ? getFeedbackLoader() : null, [feedbackSurveyId, dropdownOpened]);
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(Dropdown, {
      Button: BetaButton,
      buttonText: optOutButtonText,
      onOpenComplete: onDropdownOpen,
      "data-test-id": "dropdown",
      children: /*#__PURE__*/_jsxs(List, {
        wrapChildren: true,
        children: [showUserOptout && /*#__PURE__*/_jsx(Button, {
          onClick: () => dispatch({
            type: ACTION_USER_OPT_OUT
          }),
          "data-test-id": "userOptoutButton",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "ui-addon-opt.optout.options.userOptout"
          })
        }), showHubOptout && /*#__PURE__*/_jsx(Button, {
          onClick: () => dispatch({
            type: ACTION_HUB_OPT_OUT
          }),
          "data-test-id": "hubOptoutButton",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "ui-addon-opt.optout.options.hubOptout"
          })
        }), showHubOptin && /*#__PURE__*/_jsx(Button, {
          onClick: () => dispatch({
            type: ACTION_HUB_OPT_IN
          }),
          "data-test-id": "hubOptinButton",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "ui-addon-opt.optin.options.hubOptin"
          })
        }), learnMore && /*#__PURE__*/_jsx(Button, {
          href: learnMore.url,
          onClick: onLearnMoreClick,
          target: "_blank",
          "data-test-id": "learnMoreButton",
          children: learnMore.text
        }), feedbackSurveyAvailable && /*#__PURE__*/_jsx(Button, {
          onClick: onFeedbackSurveyClickCb,
          "data-test-id": "submitFeedbackButton",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "ui-addon-opt.optout.options.submitFeedback"
          })
        }), additionalOptions && additionalOptions.map(({
          text,
          onClick
        }, idx) => /*#__PURE__*/_jsx(Button, {
          onClick: onClick,
          "data-test-id": `additionalOptionsButton${idx}`,
          children: text
        }, `option-${idx}`)), /*#__PURE__*/_jsx(Button, {
          onClick: onMinimized,
          "data-test-id": "minimizeButton",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "ui-addon-opt.common.options.minimize"
          })
        })]
      })
    }), FeedbackLoader && /*#__PURE__*/_jsx(FeedbackLoader, {
      onClientLoad: onFeedbackClientLoadCb
    })]
  });
};
Optout.propTypes = {
  additionalOptions: AdditionalOptionsPropType,
  dispatch: PropTypes.func.isRequired,
  feedbackSurveyId: PropTypes.string,
  learnMore: PropTypes.shape({
    text: PropTypes.node.isRequired,
    url: PropTypes.string.isRequired
  }),
  onLearnMoreClick: PropTypes.func,
  onMinimized: PropTypes.func,
  onView: PropTypes.func,
  optinName: PropTypes.string.isRequired,
  optOutButtonText: PropTypes.node,
  showHubOptin: PropTypes.bool,
  showHubOptout: PropTypes.bool,
  showUserOptout: PropTypes.bool,
  usageTracker: PropTypes.shape({
    standard: PropTypes.shape({
      clone: PropTypes.func.isRequired,
      track: PropTypes.func.isRequired
    }),
    immediate: PropTypes.shape({
      clone: PropTypes.func.isRequired,
      track: PropTypes.func.isRequired
    })
  })
};
export default Optout;